import PropTypes from 'prop-types';
import { Layout } from 'react-admin';
import { useTheme, useMediaQuery } from '@mui/material';
import { useMemo, useState } from 'react';
import { CustomAppBar } from '@/components/global/layout/CustomAppBar';
import { CustomMenu } from '@/components/global/layout/CustomMenu';
import { CustomSidebar } from '@/components/global/layout/CustomSidebar';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { globalStyles } from '@/themes/commonStyles/global';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useDialogResizer } from '@/composables/useDialogResizer';
import { useAutoScrollLock } from '@/composables/useAutoScrollLock';
import { LayoutSidebarProvider, useLayoutSidebar } from '@/layouts/layoutSidebarContext.jsx';

const LayoutAppBar = props => {
    const { toggleSidebar, isSidebarOpen } = useLayoutSidebar();
    return <CustomAppBar {...props} toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />;
};

const LayoutSidebar = props => {
    const { isSidebarOpen } = useLayoutSidebar();
    return <CustomSidebar {...props} open={isSidebarOpen} />;
};

const LayoutMenu = props => {
    const { closeSidebar, openMenus, setOpenMenus } = useLayoutSidebar();
    return <CustomMenu {...props} closeSidebar={closeSidebar} openMenus={openMenus} setOpenMenus={setOpenMenus} />;
};

export const MaasLoopLayout = ({ children }) => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [sidebarOpen, setSidebarOpen] = useState(isMdUp);
    const [openMenus, setOpenMenus] = useState({});

    useDialogResizer();
    useAutoScrollLock();

    const layoutSidebarValue = useMemo(
        () => ({
            isSidebarOpen: sidebarOpen,
            openMenus,
            setOpenMenus,
            toggleSidebar: () => setSidebarOpen(open => !open),
            closeSidebar: () => setSidebarOpen(false),
        }),
        [sidebarOpen, openMenus],
    );

    return (
        <LayoutSidebarProvider value={layoutSidebarValue}>
            <Layout
                appBar={LayoutAppBar}
                sidebar={LayoutSidebar}
                menu={LayoutMenu}
                p={0}
                className='MaasLoopLayout'
                sx={{
                    '& .RaLayout-appFrame': {
                        marginTop: 0,
                    },
                    '& .RaLayout-content': {
                        backgroundColor: Palette.gray.gray75,
                        '& .wrapper': {
                            paddingBlock: theme.spacing(8),
                            paddingInline: theme.spacing(5),
                            backgroundColor: Palette.gray.gray75,
                            [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                                padding: theme.spacing(10),
                            },
                            '& .MuiPaper-root.MuiCard-root': {
                                backgroundColor: 'transparent',
                                overflow: 'visible',
                                '& .MuiStack-root': {
                                    margin: '0 0 33px 0',
                                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                        margin: '0',
                                        gap: '2px',
                                    },
                                },
                                '& .MuiGrid-root.MuiGrid-container': {
                                    backgroundColor: Palette.white,
                                    padding: theme.spacing(5),
                                },
                            },
                            '& .RaEdit-main': {
                                backgroundColor: Palette.white,
                            },
                        },
                        '& .wrapper:has(.MuiToolbar-root.RaBulkActionsToolbar-toolbar:not(.RaBulkActionsToolbar-collapsed))': {
                            paddingBottom: '114px',
                        },
                    },
                    '& .autocomplete-parent': {
                        ...globalStyles.autocompleteParent,
                    },
                }}
            >
                <div className='wrapper'>{children}</div>
            </Layout>
        </LayoutSidebarProvider>
    );
};

MaasLoopLayout.propTypes = {
    children: PropTypes.node.isRequired,
};

export default MaasLoopLayout;
